import { useTranslation } from "react-i18next"

import { DashboardKpiTile } from "@/components/brand/dashboard/dashboard-kpi-tile"
import type { BrandDashboardKpis } from "@/types/brand-dashboard"

/**
 * OKS-38 — KPI strip section for the brand dashboard.
 *
 * Renders a 4-tile grid wrapped in a `<section aria-label>` per UX Spec
 * §6. Breakpoints (UX Spec §5):
 *   - `<sm` (mobile, <640px) → 1 column stack
 *   - `sm..lg` (640..1023px) → 2-col × 2-row
 *   - `≥lg` (≥1024px)        → 4-col × 1-row
 *
 * The 4 tiles render in fixed order: Active Crew, Active Missions,
 * Completion Rate, Content This Month. `null` values are honoured for
 * `completion_rate` only (renders "—" via `<DashboardKpiTile>`).
 */
export interface DashboardKpiStripProps {
  kpis: BrandDashboardKpis
}

export function DashboardKpiStrip({ kpis }: DashboardKpiStripProps) {
  const { t } = useTranslation()

  return (
    <section
      aria-label={t("brand_home.kpis.section_aria")}
      data-testid="dashboard-kpi-strip"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      <DashboardKpiTile
        labelKey="brand_home.kpis.active_crew.label"
        ariaLabelKey="brand_home.kpis.active_crew.aria"
        value={kpis.active_crew}
        kind="integer"
        testId="dashboard-kpi-tile-active_crew"
      />
      <DashboardKpiTile
        labelKey="brand_home.kpis.active_missions.label"
        ariaLabelKey="brand_home.kpis.active_missions.aria"
        value={kpis.active_missions}
        kind="integer"
        testId="dashboard-kpi-tile-active_missions"
      />
      <DashboardKpiTile
        labelKey="brand_home.kpis.completion_rate.label"
        ariaLabelKey="brand_home.kpis.completion_rate.aria"
        emptyAriaKey="brand_home.kpis.completion_rate.empty_aria"
        value={kpis.completion_rate}
        kind="percent"
        testId="dashboard-kpi-tile-completion_rate"
      />
      <DashboardKpiTile
        labelKey="brand_home.kpis.content_this_month.label"
        ariaLabelKey="brand_home.kpis.content_this_month.aria"
        value={kpis.content_this_month}
        kind="integer"
        testId="dashboard-kpi-tile-content_this_month"
      />
    </section>
  )
}
